import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  Task,
  Setting,
  UserProfile,
  TimeEntry,
  Tag,
  Project,
  Section,
  CreateTask,
  CreateSetting,
  CreateUserProfile,
  CreateTimeEntry,
  CreateTag,
  CreateProject,
  UpdateTask,
  UpdateSetting,
  UpdateUserProfile,
  UpdateTimeEntry,
  UpdateTag,
  UpdateProject,
  TaskStatus,
  AttentionLevel,
  TaskCategory
} from './models'
import { db } from './indexeddb'

// Base hook for loading states
interface UseAsyncState<T> {
  data: T | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

// Generic async hook
function useAsync<T>(
  asyncFunction: () => Promise<T>,
  dependencies: any[] = []
): UseAsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const execute = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await asyncFunction()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, dependencies)

  useEffect(() => {
    execute()
  }, [execute])

  return { data, loading, error, refetch: execute }
}

// Task hooks
export function useTasks(): UseAsyncState<Task[]> & {
  createTask: (task: CreateTask) => Promise<Task>
  updateTask: (id: string, updates: Partial<UpdateTask>) => Promise<Task | null>
  deleteTask: (id: string) => Promise<void>
  completeTask: (id: string) => Promise<Task | null>
} {
  const asyncState = useAsync(() => db.getAllTasks())

  const createTask = useCallback(async (taskData: CreateTask) => {
    const task = await db.createTask(taskData)
    await asyncState.refetch()
    return task
  }, [asyncState.refetch])

  const updateTask = useCallback(async (id: string, updates: Partial<UpdateTask>) => {
    const task = await db.updateTask(id, updates)
    await asyncState.refetch()
    return task
  }, [asyncState.refetch])

  const deleteTask = useCallback(async (id: string) => {
    await db.deleteTask(id)
    await asyncState.refetch()
  }, [asyncState.refetch])

  const completeTask = useCallback(async (id: string) => {
    const task = await db.completeTask(id)
    await asyncState.refetch()
    return task
  }, [asyncState.refetch])

  return {
    ...asyncState,
    createTask,
    updateTask,
    deleteTask,
    completeTask
  }
}

export function useTask(id: string | null): UseAsyncState<Task> {
  return useAsync(() => {
    if (!id) throw new Error('No task ID provided')
    return db.getTask(id).then(task => {
      if (!task) throw new Error('Task not found')
      return task
    })
  }, [id])
}

export function useTasksByStatus(status: TaskStatus): UseAsyncState<Task[]> {
  return useAsync(() => db.getTasksByStatus(status), [status])
}

export function useTasksByCategory(category: TaskCategory): UseAsyncState<Task[]> {
  return useAsync(() => db.getTasksByCategory(category), [category])
}

// Settings hooks
export function useSettings(): UseAsyncState<Setting[]> & {
  getSetting: <T = any>(key: string, defaultValue?: T) => Promise<T>
  setSetting: (key: string, value: any, type?: Setting['type'], description?: string) => Promise<Setting>
} {
  const asyncState = useAsync(() => db.getAllSettings())

  const getSetting = useCallback(async <T = any>(key: string, defaultValue?: T): Promise<T> => {
    return await db.getSettingValue(key, defaultValue)
  }, [])

  const setSetting = useCallback(async (
    key: string, 
    value: any, 
    type: Setting['type'] = 'string', 
    description?: string
  ) => {
    const setting = await db.setSetting(key, value, type, description)
    await asyncState.refetch()
    return setting
  }, [asyncState.refetch])

  return {
    ...asyncState,
    getSetting,
    setSetting
  }
}

export function useSetting<T = any>(key: string, defaultValue?: T): {
  value: T | null
  loading: boolean
  error: Error | null
  setValue: (newValue: T) => Promise<void>
} {
  const [value, setValue] = useState<T | null>(defaultValue !== undefined ? defaultValue : null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const loadSetting = async () => {
      try {
        setLoading(true)
        const result = await db.getSettingValue<T>(key, defaultValue)
        setValue(result)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load setting'))
      } finally {
        setLoading(false)
      }
    }

    loadSetting()
  }, [key, defaultValue])

  const updateValue = useCallback(async (newValue: T) => {
    try {
      await db.setSetting(key, newValue, typeof newValue as Setting['type'])
      setValue(newValue)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update setting'))
    }
  }, [key])

  return { value, loading, error, setValue: updateValue }
}

// User Profile hooks
export function useUserProfile(): UseAsyncState<UserProfile> & {
  createProfile: (profile: CreateUserProfile) => Promise<UserProfile>
  updateProfile: (id: string, updates: Partial<UpdateUserProfile>) => Promise<UserProfile | null>
} {
  const asyncState = useAsync(() => {
    return db.getCurrentUserProfile().then(profile => {
      if (!profile) throw new Error('No user profile found')
      return profile
    })
  })

  const createProfile = useCallback(async (profileData: CreateUserProfile) => {
    const profile = await db.createUserProfile(profileData)
    await asyncState.refetch()
    return profile
  }, [asyncState.refetch])

  const updateProfile = useCallback(async (id: string, updates: Partial<UpdateUserProfile>) => {
    const profile = await db.updateUserProfile(id, updates)
    await asyncState.refetch()
    return profile
  }, [asyncState.refetch])

  return {
    ...asyncState,
    createProfile,
    updateProfile
  }
}

// Time Entry hooks
export function useTimeEntries(): UseAsyncState<TimeEntry[]> & {
  createEntry: (entry: CreateTimeEntry) => Promise<TimeEntry>
  stopEntry: (id: string) => Promise<TimeEntry | null>
} {
  const asyncState = useAsync(() => db.timeEntries.toArray())

  const createEntry = useCallback(async (entryData: CreateTimeEntry) => {
    const entry = await db.createTimeEntry(entryData)
    await asyncState.refetch()
    return entry
  }, [asyncState.refetch])

  const stopEntry = useCallback(async (id: string) => {
    const entry = await db.stopTimeEntry(id)
    await asyncState.refetch()
    return entry
  }, [asyncState.refetch])

  return {
    ...asyncState,
    createEntry,
    stopEntry
  }
}

export function useActiveTimeEntry(): UseAsyncState<TimeEntry> {
  return useAsync(() => {
    return db.getActiveTimeEntry().then(entry => {
      if (!entry) throw new Error('No active time entry')
      return entry
    })
  })
}

export function useTimeEntriesForTask(taskId: string): UseAsyncState<TimeEntry[]> {
  return useAsync(() => db.getTimeEntriesForTask(taskId), [taskId])
}

// Tag hooks
export function useTags(): UseAsyncState<Tag[]> & {
  createTag: (tag: CreateTag) => Promise<Tag>
  getOrCreateTag: (name: string, color?: string) => Promise<Tag>
} {
  const asyncState = useAsync(() => db.getAllTags())

  const createTag = useCallback(async (tagData: CreateTag) => {
    const tag = await db.createTag(tagData)
    await asyncState.refetch()
    return tag
  }, [asyncState.refetch])

  const getOrCreateTag = useCallback(async (name: string, color?: string) => {
    const tag = await db.getOrCreateTag(name, color)
    await asyncState.refetch()
    return tag
  }, [asyncState.refetch])

  return {
    ...asyncState,
    createTag,
    getOrCreateTag
  }
}

// Project hooks
export function useProjects(): UseAsyncState<Project[]> & {
  createProject: (project: CreateProject) => Promise<Project>
} {
  const asyncState = useAsync(() => db.getAllProjects())

  const createProject = useCallback(async (projectData: CreateProject) => {
    const project = await db.createProject(projectData)
    await asyncState.refetch()
    return project
  }, [asyncState.refetch])

  return {
    ...asyncState,
    createProject
  }
}

export function useProjectsByLifeSpace(lifeSpace: string): {
  data: Project[] | null
  loading: boolean
  createProject: (project: CreateProject) => Promise<Project>
  updateProject: (id: string, updates: Partial<UpdateProject>) => Promise<Project | null>
  deleteProject: (id: string) => Promise<void>
} {
  // useLiveQuery auto-reacts to any Dexie write on the projects table
  const projects = useLiveQuery(
    () => db.getProjectsByLifeSpace(lifeSpace),
    [lifeSpace]
  )

  const createProject = useCallback(async (projectData: CreateProject) => {
    return await db.createProject(projectData)
  }, [])

  const updateProject = useCallback(async (id: string, updates: Partial<UpdateProject>) => {
    return await db.updateProject(id, updates)
  }, [])

  const deleteProject = useCallback(async (id: string) => {
    await db.deleteProject(id)
  }, [])

  return {
    data: projects ?? null,
    loading: projects === undefined,
    createProject,
    updateProject,
    deleteProject,
  }
}

export function useProject(id: string | null): {
  data: Project | null
  loading: boolean
  updateProject: (updates: Partial<UpdateProject>) => Promise<Project | null>
} {
  const project = useLiveQuery(
    () => (id ? db.getProject(id) : undefined),
    [id]
  )

  const updateProject = useCallback(async (updates: Partial<UpdateProject>) => {
    if (!id) return null
    return await db.updateProject(id, updates)
  }, [id])

  return {
    data: project ?? null,
    loading: project === undefined,
    updateProject,
  }
}

// Statistics and analytics hooks
export function useTaskStatistics() {
  const { data: tasks } = useTasks()

  const statistics = useMemo(() => {
    if (!tasks) return null

    const totalTasks = tasks.length
    const completedTasks = tasks.filter(t => t.status === 'completed').length
    const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length
    const todoTasks = tasks.filter(t => t.status === 'todo').length
    
    const tasksByAttention = {
      high: tasks.filter(t => t.attention === 'high').length,
      medium: tasks.filter(t => t.attention === 'medium').length,
      low: tasks.filter(t => t.attention === 'low').length
    }

    const tasksByCategory = {
      work: tasks.filter(t => t.category === 'work').length,
      personal: tasks.filter(t => t.category === 'personal').length,
      health: tasks.filter(t => t.category === 'health').length,
      learning: tasks.filter(t => t.category === 'learning').length,
      other: tasks.filter(t => t.category === 'other').length
    }

    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      todoTasks,
      tasksByAttention,
      tasksByCategory,
      completionRate
    }
  }, [tasks])

  return statistics
}

// Database utility hooks
export function useDatabase() {
  const clearAllData = useCallback(async () => {
    await db.clearAllData()
  }, [])

  const getUnsyncedItems = useCallback(async () => {
    return await db.getUnSyncedItems()
  }, [])

  return {
    clearAllData,
    getUnsyncedItems,
    db
  }
}

/**
 * Live-query sections for a specific project.
 * Only returns saved sections (non-empty title, not deleted).
 * Pass `undefined` as projectId to skip the query.
 */
export function useSectionsByProject(projectId: string | undefined): Section[] {
  const sections = useLiveQuery(
    () => projectId ? db.getSectionsByProject(projectId) : [],
    [projectId]
  )
  return useMemo(
    () => (sections ?? []).filter(s => s.title.trim() !== ''),
    [sections]
  )
}